<template>
  <div class="salesforce-settings">

    <!-- Configurations List -->
    <div class="configurations-list">
      <div v-if="loading" class="text-center py-8">
        <i class="fas fa-spinner fa-spin text-3xl"></i>
      </div>

      <div v-else-if="configurations.length === 0" class="text-center py-12 text-gray-500">
        No configurations found. Create your first configuration to get started.
      </div>

      <div v-else class="grid gap-4">
        <div 
          v-for="config in configurations" 
          :key="config.id"
          class="configuration-card bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 p-6 border border-gray-100"
        >
          <div class="flex justify-between items-start">
            <div>
              <h3 class="text-lg font-semibold mb-2 text-gray-900">
                {{ config.foundation_campaign?.name || 'Unknown Foundation' }}
              </h3>
              <div class="text-sm text-gray-700">
                <span class="mr-4">
                  <i class="fas fa-map-signs mr-1"></i>
                  {{ config.field_mappings?.length || 0 }} field mappings
                </span>
                <span class="mr-4">
                  <i class="fas fa-sync mr-1"></i>
                  Last sync: {{ config.last_sync_human || 'Never' }}
                </span>
                <span v-if="config.total_syncs > 0" class="text-xs">
                  ({{ config.successful_syncs }}/{{ config.total_syncs }} successful)
                </span>
              </div>
            </div>
            
            <div class="flex items-center space-x-2">
              <!-- Enable/Disable Toggle -->
              <label class="switch">
                <input 
                  type="checkbox" 
                  :checked="config.enabled"
                  @change="toggleConfiguration(config)"
                >
                <span class="slider round"></span>
              </label>
              
              <!-- Action Buttons -->
              <button 
                @click="editConfiguration(config)"
                class="btn btn-sm btn-secondary"
                title="Edit"
              >
                <i class="fas fa-edit"></i>
              </button>
              
              <button 
                @click="testConnection(config)"
                class="btn btn-sm btn-info"
                title="Test Connection"
              >
                <i class="fas fa-plug"></i>
              </button>
              
              <button 
                @click="viewLogs(config)"
                class="btn btn-sm btn-warning"
                title="View Logs"
              >
                <i class="fas fa-history"></i>
              </button>
              
              <button 
                @click="duplicateConfiguration(config)"
                class="btn btn-sm btn-primary"
                title="Duplicate Configuration"
              >
                <i class="fas fa-copy"></i>
              </button>
              
              <button 
                @click="deleteConfiguration(config)"
                class="btn btn-sm btn-danger"
                title="Delete"
              >
                <i class="fas fa-trash"></i>
              </button>
            </div>
          </div>
          
          <!-- Status Indicator -->
          <div class="mt-4">
            <span 
              class="inline-block px-3 py-1 text-xs font-medium rounded-full"
              :class="getStatusClass(config)"
            >
              {{ getStatusText(config) }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Create/Edit Modal -->
    <div 
      v-if="showCreateModal || editingConfig"
      class="modal-overlay fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      @click.self="closeModal"
    >
      <div class="modal-content bg-white rounded-lg p-4 sm:p-6 max-w-6xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        <TabbedConfiguration
          :configuration="editingConfig"
          :foundations="foundations"
          :templates="templates"
          @save="saveConfiguration"
          @cancel="closeModal"
        />
      </div>
    </div>

    <!-- Logs Modal -->
    <div 
      v-if="showLogsModal"
      class="modal-overlay fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      @click.self="showLogsModal = false"
    >
      <div class="modal-content bg-white rounded-lg p-4 sm:p-6 max-w-6xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        <SyncHistory
          :configuration-id="viewingLogsForConfig"
          @close="showLogsModal = false"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import axios from 'axios';
import TabbedConfiguration from './TabbedConfiguration.vue';
import SyncHistory from './SyncHistory.vue';

export default {
  name: 'SalesforceSettings',
  components: {
    TabbedConfiguration,
    SyncHistory,
  },
  setup() {
    const configurations = ref([]);
    const foundations = ref([]);
    const templates = ref([]);
    const loading = ref(true);
    const foundationsLoading = ref(true);
    const showCreateModal = ref(false);
    const editingConfig = ref(null);
    const showLogsModal = ref(false);
    const viewingLogsForConfig = ref(null);

    const fetchConfigurations = async () => {
      try {
        loading.value = true;
        const response = await axios.get('/admin/salesforce/configurations');
        console.log('Fetched configurations:', response.data);
        
        // Log details about field mappings
        if (response.data && response.data.length > 0) {
          response.data.forEach((config, index) => {
            console.log(`Config ${index} - field_mappings:`, config.field_mappings);
          });
        }
        
        configurations.value = response.data;
      } catch (error) {
        console.error('Failed to fetch configurations:', error);
        alert('Failed to load configurations');
      } finally {
        loading.value = false;
      }
    };

    const fetchFoundations = async () => {
      foundationsLoading.value = true;
      try {
        const response = await axios.get('/admin/salesforce/foundations');
        foundations.value = response.data;
        console.log('Foundations loaded:', foundations.value.length);
      } catch (error) {
        console.error('Failed to fetch foundations:', error);
        foundations.value = [];
      } finally {
        foundationsLoading.value = false;
      }
    };

    const fetchTemplates = async () => {
      try {
        const response = await axios.get('/admin/salesforce/templates');
        templates.value = response.data;
      } catch (error) {
        console.error('Failed to fetch templates:', error);
      }
    };

    const toggleConfiguration = async (config) => {
      try {
        await axios.put(`/admin/salesforce/${config.id}`, {
          enabled: !config.enabled,
        });
        config.enabled = !config.enabled;
      } catch (error) {
        console.error('Failed to toggle configuration:', error);
        alert('Failed to update configuration');
      }
    };

    const editConfiguration = (config) => {
      console.log('Editing configuration:', config);
      console.log('Configuration foundation_campaign_id:', config.foundation_campaign_id);
      console.log('Field mappings:', config.field_mappings);
      console.log('Available foundations for edit:', foundations.value);
      editingConfig.value = config;
    };

    const duplicateConfiguration = (config) => {
      // Create a deep copy of the configuration
      const duplicatedConfig = {
        foundation_campaign_id: null, // Clear so user must select new foundation
        field_mappings: [...(config.field_mappings || [])] // Copy field mappings
      };
      
      // Set as editing config to open the modal
      editingConfig.value = duplicatedConfig;
      
      // Optionally show a message
      console.log('Duplicating configuration from:', config.foundation_campaign?.name);
    };

    const deleteConfiguration = async (config) => {
      if (!confirm(`Are you sure you want to delete the configuration for ${config.foundation_campaign?.name}?`)) {
        return;
      }

      try {
        await axios.delete(`/admin/salesforce/${config.id}`);
        configurations.value = configurations.value.filter(c => c.id !== config.id);
      } catch (error) {
        console.error('Failed to delete configuration:', error);
        alert('Failed to delete configuration');
      }
    };

    const testConnection = async (config) => {
      try {
        const response = await axios.post('/admin/salesforce/test-connection', {
          configuration_id: config.id,
        });
        
        if (response.data.success) {
          let message = 'Connection successful!';
          if (response.data.details) {
            if (response.data.details.instance_url) {
              message += `\nInstance: ${response.data.details.instance_url}`;
            }
            if (response.data.details.api_version) {
              message += `\nAPI Version: ${response.data.details.api_version}`;
            }
          }
          alert(message);
        } else {
          let message = `Connection failed: ${response.data.message}`;
          if (response.data.errors && response.data.errors.length > 0) {
            message += '\n\nErrors:\n' + response.data.errors.join('\n');
          }
          if (response.data.troubleshooting && response.data.troubleshooting.length > 0) {
            message += '\n\nTroubleshooting:\n' + response.data.troubleshooting.join('\n');
          }
          alert(message);
        }
      } catch (error) {
        console.error('Connection test failed:', error);
        let message = 'Connection test failed';
        if (error.response && error.response.data) {
          if (error.response.data.message) {
            message = error.response.data.message;
          }
          if (error.response.data.errors && error.response.data.errors.length > 0) {
            message += '\n\nErrors:\n' + error.response.data.errors.join('\n');
          }
        }
        alert(message);
      }
    };

    const viewLogs = (config) => {
      viewingLogsForConfig.value = config.id;
      showLogsModal.value = true;
    };

    const saveConfiguration = async (data) => {
      try {
        console.log('Saving configuration with data:', data);
        console.log('Field mappings being sent:', data.field_mappings);
        console.log('Field mappings count:', data.field_mappings?.length || 0);
        
        let response;
        if (editingConfig.value?.id) {
          console.log('Updating configuration ID:', editingConfig.value.id);
          response = await axios.put(`/admin/salesforce/${editingConfig.value.id}`, data);
        } else {
          console.log('Creating new configuration');
          response = await axios.post('/admin/salesforce/store', data);
        }
        
        if (response.data.success) {
          await fetchConfigurations();
          closeModal();
        } else {
          alert('Failed to save configuration: ' + response.data.message);
        }
      } catch (error) {
        console.error('Failed to save configuration:', error);
        if (error.response && error.response.data) {
          console.error('Validation errors:', error.response.data);
          if (error.response.data.errors) {
            const errorMessages = Object.entries(error.response.data.errors)
              .map(([field, messages]) => `${field}: ${messages.join(', ')}`)
              .join('\n');
            alert('Validation errors:\n' + errorMessages);
          } else if (error.response.data.message) {
            alert('Error: ' + error.response.data.message);
          } else {
            alert('Failed to save configuration');
          }
        } else {
          alert('Failed to save configuration');
        }
      }
    };

    const closeModal = () => {
      showCreateModal.value = false;
      editingConfig.value = null;
    };

    const getLastSyncTime = (config) => {
      return config.last_sync_human || 'Never';
    };

    const getStatusClass = (config) => {
      if (!config.enabled) {
        return 'bg-gray-100 text-gray-700';
      }
      
      // Check sync health
      if (config.failed_syncs > 0 && config.failed_syncs > config.successful_syncs) {
        return 'bg-red-100 text-red-800';
      } else if (config.successful_syncs > 0) {
        return 'bg-green-100 text-green-800';
      }
      
      return 'bg-blue-100 text-blue-800';
    };

    const getStatusText = (config) => {
      if (!config.enabled) {
        return 'Disabled';
      }
      
      // Check sync health
      if (config.failed_syncs > 0 && config.failed_syncs > config.successful_syncs) {
        return 'Errors';
      } else if (config.successful_syncs > 0) {
        return 'Active';
      }
      
      return 'Ready';
    };

    onMounted(async () => {
      await Promise.all([
        fetchConfigurations(),
        fetchFoundations(),
        fetchTemplates()
      ]);
    });

    return {
      configurations,
      foundations,
      templates,
      loading,
      foundationsLoading,
      showCreateModal,
      editingConfig,
      showLogsModal,
      viewingLogsForConfig,
      toggleConfiguration,
      editConfiguration,
      duplicateConfiguration,
      deleteConfiguration,
      testConnection,
      viewLogs,
      saveConfiguration,
      closeModal,
      getLastSyncTime,
      getStatusClass,
      getStatusText,
    };
  },
};
</script>

<style scoped>
.switch {
  position: relative;
  display: inline-block;
  width: 50px;
  height: 24px;
}

.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: .4s;
}

.slider.round {
  border-radius: 24px;
}

.slider.round:before {
  border-radius: 50%;
}

.slider:before {
  position: absolute;
  content: "";
  height: 16px;
  width: 16px;
  left: 4px;
  bottom: 4px;
  background-color: white;
  transition: .4s;
}

input:checked + .slider {
  background-color: #2196F3;
}

input:checked + .slider:before {
  transform: translateX(26px);
}

.btn {
  @apply px-4 py-2 rounded-lg font-medium transition-all duration-150 shadow-sm hover:shadow-md;
}

.btn-primary {
  @apply bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500;
}

.btn-secondary {
  @apply bg-gray-600 text-white hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500;
}

.btn-info {
  @apply bg-cyan-600 text-white hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500;
}

.btn-warning {
  @apply bg-amber-600 text-white hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500;
}

.btn-danger {
  @apply bg-red-600 text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500;
}

.btn-sm {
  @apply px-3 py-1.5 text-sm rounded-md;
}
</style>